// This environment's engine jobs on the System overview: NextAdmin `tables/basic-tables` in a card. Every scheduled job
// with its last run, how long it took, what failed and which ticks it skipped — from the records the engine's scheduler
// keeps (owner 2026-10-27: "make everything transparent").
import { Badge, Card, DataTable, type DataTableColumn } from '../../../components/ui/index.js';
import type { SystemEngineJob } from '../../../generated/api/types.gen.js';
import { formatAgo, formatCount, formatSeconds } from './metric-format.js';

const ms = (value: number | null) => (value === null ? null : value / 1000);

function JobState({ job }: { job: SystemEngineJob }) {
  if (!job.enabled) return <Badge variant="neutral">Off</Badge>;
  if (job.runningSeconds !== null) {
    return (
      <Badge variant="info" dot>
        {`Running ${formatSeconds(job.runningSeconds)}`}
      </Badge>
    );
  }
  if (job.lastOutcome === 'failed') {
    return (
      <Badge variant="danger" dot>
        Failed
      </Badge>
    );
  }
  if (job.lastOutcome === 'succeeded') {
    return (
      <Badge variant="success" dot>
        Succeeded
      </Badge>
    );
  }
  return <Badge variant="neutral">Not run yet</Badge>;
}

function Skipped({ job }: { job: SystemEngineJob }) {
  const { still_running: overran, other_replica: replica, egress_hold: egress } = job.skippedLastDay;
  if (overran + replica + egress === 0) return <>—</>;
  return (
    <span className="inline-flex flex-wrap justify-end gap-1">
      {overran > 0 && (
        <Badge variant="warning" dot>
          {`${formatCount(overran)} overran`}
        </Badge>
      )}
      {replica > 0 && <Badge variant="neutral">{`${formatCount(replica)} other replica`}</Badge>}
      {egress > 0 && <Badge variant="neutral">{`${formatCount(egress)} egress hold`}</Badge>}
    </span>
  );
}

function columns(nowMs: number): DataTableColumn<SystemEngineJob>[] {
  return [
    { key: 'name', header: 'Job', render: (j) => <span className="font-mono text-xs">{j.name}</span> },
    { key: 'schedule', header: 'Schedule', render: (j) => <span className="font-mono text-xs">{j.schedule}</span> },
    { key: 'state', header: 'Last run', render: (j) => <JobState job={j} /> },
    {
      key: 'started',
      header: 'Started',
      sortValue: (j) => (j.lastStartedAt ? Date.parse(j.lastStartedAt) : null),
      render: (j) => formatAgo(j.lastStartedAt, nowMs),
    },
    {
      key: 'took',
      header: 'Took',
      align: 'right',
      sortValue: (j) => j.lastDurationMs,
      render: (j) => formatSeconds(ms(j.lastDurationMs)),
    },
    {
      key: 'typical',
      header: 'Usually',
      align: 'right',
      sortValue: (j) => j.typicalMs,
      render: (j) => formatSeconds(ms(j.typicalMs)),
    },
    {
      key: 'longest',
      header: 'Longest recent',
      align: 'right',
      sortValue: (j) => j.longestRecentMs,
      render: (j) => formatSeconds(ms(j.longestRecentMs)),
    },
    {
      key: 'runs',
      header: 'Runs / failed',
      align: 'right',
      sortValue: (j) => j.failures,
      render: (j) => `${formatCount(j.runs)} / ${formatCount(j.failures)}`,
    },
    { key: 'skipped', header: 'Skipped today', align: 'right', render: (j) => <Skipped job={j} /> },
    {
      key: 'error',
      header: 'Last error',
      render: (j) =>
        j.lastError ? (
          <span className="line-clamp-2 max-w-sm text-xs text-danger" title={j.lastError}>
            {j.lastError}
          </span>
        ) : (
          '—'
        ),
    },
  ];
}

export function EngineJobsCard({ jobs, nowMs }: { jobs: SystemEngineJob[]; nowMs: number }) {
  return (
    <div id="engine-jobs" className="xl:col-span-12">
      <Card title={`Engine jobs (${jobs.length})`}>
        <DataTable
          columns={columns(nowMs)}
          rows={jobs}
          rowKey={(j) => j.name}
          pageSize={20}
          getRowAttention={(j) =>
            j.enabled && j.lastOutcome === 'failed'
              ? 'danger'
              : j.skippedLastDay.still_running > 0
                ? 'warning'
                : undefined
          }
          empty={
            <p className="py-6 text-center text-sm text-muted">
              The engine has not recorded a job yet: it records each one when it starts.
            </p>
          }
        />
      </Card>
    </div>
  );
}
